export const COOKIE_CONSENT_STORAGE_KEY = "confidacare_cookie_consent";
export const COOKIE_CONSENT_VERSION = "1.0";
export const COOKIE_CONSENT_EVENT = "confidacare:cookie-consent-change";

export type CookieConsentCategory = "necessary" | "functional" | "performance" | "targeting";

export type CookieConsent = {
  version: string;
  necessary: true;
  functional: boolean;
  performance: boolean;
  targeting: boolean;
  updatedAt: string;
};

export type CookieConsentDraft = Pick<
  CookieConsent,
  "functional" | "performance" | "targeting"
>;

const DEFAULT_DRAFT: CookieConsentDraft = {
  functional: false,
  performance: false,
  targeting: false,
};

const FUNCTIONAL_STORAGE_KEYS = [
  "confidacare-search-store-v1",
  "confidacare-results-filters-v1",
  "nudge_dismissed",
] as const;

function canUseWindow() {
  return typeof window !== "undefined";
}

function isBoolean(value: unknown): value is boolean {
  return typeof value === "boolean";
}

function normalizeConsent(value: unknown): CookieConsent | null {
  if (!value || typeof value !== "object") {
    return null;
  }

  const input = value as Partial<CookieConsent>;
  if (
    input.version !== COOKIE_CONSENT_VERSION ||
    input.necessary !== true ||
    !isBoolean(input.functional) ||
    !isBoolean(input.performance) ||
    !isBoolean(input.targeting) ||
    typeof input.updatedAt !== "string"
  ) {
    return null;
  }

  return {
    version: input.version,
    necessary: true,
    functional: input.functional,
    performance: input.performance,
    targeting: input.targeting,
    updatedAt: input.updatedAt,
  };
}

export function createCookieConsent(draft: CookieConsentDraft): CookieConsent {
  return {
    version: COOKIE_CONSENT_VERSION,
    necessary: true,
    functional: draft.functional,
    performance: draft.performance,
    targeting: draft.targeting,
    updatedAt: new Date().toISOString(),
  };
}

export function getDefaultCookieConsentDraft(): CookieConsentDraft {
  return { ...DEFAULT_DRAFT };
}

export function getCookieConsent(): CookieConsent | null {
  if (!canUseWindow()) {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    return normalizeConsent(JSON.parse(raw));
  } catch {
    return null;
  }
}

function broadcastCookieConsent(consent: CookieConsent | null) {
  if (!canUseWindow()) {
    return;
  }

  window.dispatchEvent(
    new CustomEvent(COOKIE_CONSENT_EVENT, {
      detail: consent,
    }),
  );
}

function pruneDisallowedOptionalStorage(consent: CookieConsent) {
  if (!canUseWindow()) {
    return;
  }

  try {
    if (!consent.functional) {
      FUNCTIONAL_STORAGE_KEYS.forEach((key) => {
        window.localStorage.removeItem(key);
      });
    }
  } catch {}
}

export function saveCookieConsent(consent: CookieConsent) {
  if (!canUseWindow()) {
    return;
  }

  pruneDisallowedOptionalStorage(consent);
  window.localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, JSON.stringify(consent));
  broadcastCookieConsent(consent);
}

export function clearCookieConsent() {
  if (!canUseWindow()) {
    return;
  }

  window.localStorage.removeItem(COOKIE_CONSENT_STORAGE_KEY);
  broadcastCookieConsent(null);
}

export function shouldShowCookieBanner() {
  return getCookieConsent() === null;
}

export function hasConsent(category: CookieConsentCategory) {
  if (category === "necessary") {
    return true;
  }

  const consent = getCookieConsent();
  if (!consent) {
    return false;
  }

  return consent[category];
}

export function getCookieConsentDraftFromStored(): CookieConsentDraft {
  const consent = getCookieConsent();
  if (!consent) {
    return getDefaultCookieConsentDraft();
  }

  return {
    functional: consent.functional,
    performance: consent.performance,
    targeting: consent.targeting,
  };
}

export function getOptionalStorageItem(
  key: string,
  category: Exclude<CookieConsentCategory, "necessary">,
) {
  if (!canUseWindow() || !hasConsent(category)) {
    return null;
  }

  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function setOptionalStorageItem(
  key: string,
  value: string,
  category: Exclude<CookieConsentCategory, "necessary">,
) {
  if (!canUseWindow()) {
    return;
  }

  try {
    if (hasConsent(category)) {
      window.localStorage.setItem(key, value);
      return;
    }

    window.localStorage.removeItem(key);
  } catch {}
}

export function removeOptionalStorageItem(key: string) {
  if (!canUseWindow()) {
    return;
  }

  try {
    window.localStorage.removeItem(key);
  } catch {}
}

export function createConsentAwareStorage(
  category: Exclude<CookieConsentCategory, "necessary">,
): Storage {
  const noop = () => {};

  return {
    get length() {
      if (!canUseWindow() || !hasConsent(category)) {
        return 0;
      }

      return window.localStorage.length;
    },
    clear: noop,
    getItem: (key: string) => getOptionalStorageItem(key, category),
    key: (index: number) => {
      if (!canUseWindow() || !hasConsent(category)) {
        return null;
      }

      return window.localStorage.key(index);
    },
    removeItem: (key: string) => {
      if (!canUseWindow()) {
        return;
      }

      try {
        window.localStorage.removeItem(key);
      } catch {}
    },
    setItem: (key: string, value: string) => {
      setOptionalStorageItem(key, value, category);
    },
  };
}
